// Envío del cuestionario de feedback + premio de imágenes — flujo migrado de
// n8n al backend nuevo (ADR-684).
//
// Flag de rollback (VITE_USE_ATLAS_BACKEND):
//  • ON  → POST {ATLAS_BACKEND_URL}/v1/feedback/cuestionario con JWT de Supabase.
//          El backend hace TODO en una transacción: insert de la respuesta +
//          acreditar las imágenes + marcar `cuestionario_respondido`. Devuelve
//          cuántas imágenes acreditó realmente (el monto lo decide el servidor).
//  • OFF → comportamiento viejo EXACTO: el cliente inserta la fila con
//          supabase-js y dispara el webhook de n8n, que acredita y marca la
//          bandera. Se conserva tal cual —incluido su fallo silencioso— porque
//          es la vía de rollback y tiene que comportarse como antes.
//
// Por qué se movió el insert al backend: antes eran tres pasos con dos actores y
// sin atomicidad (front inserta → webhook acredita y marca). Si el webhook
// fallaba quedaba una respuesta guardada SIN premio y, como la bandera no se
// marcaba, al usuario le volvía a aparecer el banner y podía responder otra vez,
// duplicando filas. Ahora o pasan las tres cosas o no pasa ninguna.

import { supabase } from './supabase';
import { atlasBackendFetch } from './atlasBackend';
import { GENERATE_AUTH_TOKEN, USE_ATLAS_BACKEND } from '../config';

const N8N_REWARD_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/4a7bff42-ee96-4579-9362-068f8567f608';

/** Premio esperado. El backend manda el real en la respuesta; esto es el fallback. */
export const FREE_IMAGES_REWARD = 10;

/** Opción "Otra" de las listas cerradas; su detalle va en columna aparte. */
export const OPCION_OTRA = 'otra';

/** "No pagamos por publicidad": excluyente con las plataformas. */
export const PUBLICIDAD_NINGUNA = 'ninguna';

/**
 * Por qué no paga publicidad — se pregunta SOLO a quien marcó `ninguna`
 * (ADR-2026-09-26-encuesta-motivo-sin-publicidad). Los ids son contrato con
 * `MOTIVO_SIN_PUBLICIDAD_OPCIONES` del backend: uno que no esté allá tira 400 y
 * el usuario pierde la encuesta entera y el premio.
 */
export const MOTIVO_SIN_PUBLICIDAD_OPCIONES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'no_puedo_pagarlo', label: 'No puedo pagarlo' },
  { id: 'no_creo_que_funcione', label: 'No creo que funcione' },
  { id: 'no_se_como_hacerlo', label: 'No sé cómo hacerlo' },
  { id: OPCION_OTRA, label: 'Otra' },
];

/** ¿La respuesta de publicidad es "no pagamos"? Es lo que abre la pregunta del motivo. */
export function noPagaPublicidad(publicidad: readonly string[]): boolean {
  return publicidad.length === 1 && publicidad[0] === PUBLICIDAD_NINGUNA;
}

export interface CuestionarioRespuestas {
  tiendaOnline: string;
  /** Detalle escrito al lado de "Otra" — se ignora si eligió otra opción. */
  tiendaOnlineOtra: string;
  publicidad: string[];
  publicidadOtra: string;
  /** Por qué no paga publicidad. Solo existe si `publicidad` es `["ninguna"]`. */
  motivoSinPublicidad: string | null;
  motivoSinPublicidadOtra: string;
  /** ¿Probó otra plataforma de IA? Las dos de abajo solo existen si es `true`. */
  proboOtraIa: boolean;
  cualOtraIa: string;
  atlasMejorQue: string;
  rolEmpresa: string;
  funcionDeseada: string;
  mejoraSugerida: string;
}

/**
 * Las dos respuestas de la rama "sí" se mandan solo si el usuario dijo que sí.
 * El backend también lo fuerza (`normalizeOtraIa`), pero acá evitamos siquiera
 * enviar el texto de una rama que el usuario descartó volviendo con "Atrás".
 */
function otraIaPayload(r: CuestionarioRespuestas) {
  return {
    probo_otra_ia: r.proboOtraIa,
    cual_otra_ia: r.proboOtraIa ? r.cualOtraIa.trim() || null : null,
    atlas_mejor_que: r.proboOtraIa ? r.atlasMejorQue.trim() || null : null,
  };
}

/**
 * Detalles de la opción "Otra". Mismo criterio: solo viajan si esa opción quedó
 * elegida, para no guardar el texto de una opción que el usuario descartó.
 */
function opcionOtraPayload(r: CuestionarioRespuestas) {
  return {
    tienda_online_otra:
      r.tiendaOnline === OPCION_OTRA ? r.tiendaOnlineOtra.trim() || null : null,
    publicidad_otra: r.publicidad.includes(OPCION_OTRA)
      ? r.publicidadOtra.trim() || null
      : null,
  };
}

/**
 * El motivo de no pagar publicidad, con el mismo criterio que la rama "sí" de
 * otra IA: solo viaja si la pregunta existió (publicidad = "no pagamos"). El
 * backend también lo fuerza (`normalizeMotivoSinPublicidad`).
 */
export function motivoSinPublicidadPayload(
  r: Pick<CuestionarioRespuestas, 'publicidad' | 'motivoSinPublicidad' | 'motivoSinPublicidadOtra'>,
) {
  const motivo = noPagaPublicidad(r.publicidad) ? r.motivoSinPublicidad : null;
  return {
    motivo_sin_publicidad: motivo,
    motivo_sin_publicidad_otra:
      motivo === OPCION_OTRA ? r.motivoSinPublicidadOtra.trim() || null : null,
  };
}

export type CuestionarioSubmitResult =
  /** Guardado y premio acreditado. `imagesCredited` es lo que dijo el servidor. */
  | { status: 'ok'; imagesCredited: number }
  /** La empresa ya había respondido: no se re-acredita, pero el banner debe irse. */
  | { status: 'already_answered' }
  /** Nada quedó guardado — el usuario puede reintentar. */
  | { status: 'error'; message: string };

const ERROR_GENERICO =
  'No pudimos guardar tu respuesta. Probá de nuevo en unos segundos.';

export async function submitCuestionario(
  idCompany: string,
  r: CuestionarioRespuestas,
): Promise<CuestionarioSubmitResult> {
  if (USE_ATLAS_BACKEND) {
    let res: Response;
    try {
      res = await atlasBackendFetch('/v1/feedback/cuestionario', {
        tienda_online: r.tiendaOnline,
        publicidad: r.publicidad,
        rol_empresa: r.rolEmpresa.trim(),
        funcion_deseada: r.funcionDeseada.trim() || null,
        mejora_sugerida: r.mejoraSugerida.trim() || null,
        ...otraIaPayload(r),
        ...opcionOtraPayload(r),
        ...motivoSinPublicidadPayload(r),
      });
    } catch {
      return { status: 'error', message: ERROR_GENERICO };
    }

    if (res.status === 409) return { status: 'already_answered' };

    if (!res.ok) {
      // 400/500: el backend hizo rollback, no quedó nada a medias.
      return { status: 'error', message: ERROR_GENERICO };
    }

    let credited = FREE_IMAGES_REWARD;
    try {
      const json = (await res.json()) as { imagenes_acreditadas?: number };
      if (typeof json?.imagenes_acreditadas === 'number') {
        credited = json.imagenes_acreditadas;
      }
    } catch {
      /* body raro pero 200: el premio se acreditó igual, usamos el default */
    }
    return { status: 'ok', imagesCredited: credited };
  }

  // ── Rollback: comportamiento viejo, tal cual estaba ────────────────────────
  try {
    const { error: insertErr } = await supabase.from('cuestionarios').insert({
      id_company: idCompany,
      tienda_online: r.tiendaOnline,
      publicidad: r.publicidad,
      rol_empresa: r.rolEmpresa.trim() || null,
      funcion_deseada: r.funcionDeseada.trim() || null,
      mejora_sugerida: r.mejoraSugerida.trim() || null,
      ...otraIaPayload(r),
      ...opcionOtraPayload(r),
      ...motivoSinPublicidadPayload(r),
    });
    if (insertErr) throw insertErr;

    // El webhook acredita y marca la bandera. Se ignora su resultado igual que
    // antes: la respuesta ya quedó guardada y el equipo puede acreditar a mano.
    try {
      await fetch(N8N_REWARD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify({ id_company: idCompany }),
      });
    } catch (webhookErr) {
      console.warn(
        '[cuestionario] Reward webhook falló (encuesta ya guardada):',
        webhookErr,
      );
    }
    return { status: 'ok', imagesCredited: FREE_IMAGES_REWARD };
  } catch (err: any) {
    return { status: 'error', message: err?.message || ERROR_GENERICO };
  }
}
